import { nanoid } from 'nanoid';
import moment from 'moment';
import {
  formatCurrencyAsText,
  numberToText,
  moneyFormat,
} from '~/utils/functions';
import { formatarCNPJ } from './functions';
import { createDateWithoutTimezone } from '~/utils/createDateWithoutTimezone ';
import { IGtin, Associado, Embalagem } from '../types';
import { tipoDivisorPreco } from '../constants';

export function formatterOnRowClick({
  row,
  apiData,
  lookups,
}: {
  row: any;
  apiData: any;
  lookups: {
    departamentos: any[];
    tipoItem: any[];
    idContabeis: any[];
    tipoMultiplicacao: any[];
    tipoNaoIncidente: any[];
    tipoIPI: any[];
    vasilhames:
      | {
          label: string;
          value: string;
        }[]
      | undefined;
    unCompras: any[];
    tipos_combustiveis: any[];
    pisCofins:
      | {
          label: string;
          value: string;
        }[]
      | undefined;
  };
}) {
  const {
    produto,
    produtoLoja,
    gtins: apiGtins,
    associados: apiAssociados,
    embalagens: apiEmbalagens,
    fornecedores: apiFornecedores,
  } = apiData;

  const {
    cod_produto,
    des_similar,
    tipo_variacao,
    tipo_movimentacao_view,
    des_medida_venda,
    des_medida_compra,
    cod_ibs_cbs,
    qtd_est_minimo,
  } = row;

  /* ==============================
     RECEITA / TARA / INFO NUTRI / MARCA
  ============================== */

  const selectedReceita =
    produtoLoja.cod_receita && produtoLoja.des_receita
      ? {
          value: produtoLoja.cod_receita,
          label: produtoLoja.des_receita,
        }
      : { value: null, label: 'Selecione a Receita' };

  const selectedTara =
    produtoLoja.cod_tara && produtoLoja.des_tara
      ? {
          value: produtoLoja.cod_tara,
          label: produtoLoja.des_tara,
        }
      : { value: null, label: 'Selecione a Tara' };

  const selectedInfoNutri =
    produto.cod_info_nutricional && produto.des_info_nutricional
      ? {
          value: produto.cod_info_nutricional,
          label: produto.des_info_nutricional,
        }
      : { value: null, label: 'Selecione a Inf. Nutr.' };

  const selectedMarca =
    produto.cod_marca && produto.des_marca
      ? {
          value: produto.cod_marca,
          label: produto.des_marca,
        }
      : { value: null, label: 'Selecione a Marca' };

  /* ==============================
     GTINS (principal primeiro)
  ============================== */

  const onlyDigits = (v: any) => String(v ?? '').replace(/\D/g, '');

  const rawPrincipal = onlyDigits(produto.cod_gtin_principal);
  const rawGtins = (apiGtins ?? []).map((g: IGtin) => onlyDigits(g.cod_gtin));

  const padLen = Math.max(
    rawPrincipal.length,
    ...rawGtins.map((x: any) => x.length),
  );

  const normalize = (v: any) => {
    const d = onlyDigits(v);
    if (!d) return '';
    return padLen > 0 ? d.padStart(padLen, '0') : d;
  };

  const cod_gtin_principal = normalize(produto.cod_gtin_principal);

  const gtins = (apiGtins ?? []).map((item: IGtin) => ({
    cod_gtin: {
      value: normalize(item.cod_gtin),
      isRequired: false,
      isInvalid: false,
      invalidLabel: '',
      // isNew: false
    },
  }));

  if (
    cod_gtin_principal &&
    !gtins.some((g: any) => g.cod_gtin.value === cod_gtin_principal)
  ) {
    gtins.unshift({
      cod_gtin: {
        value: cod_gtin_principal,
        isRequired: false,
        isInvalid: false,
        invalidLabel: '',
      },
    });
  }

  // principal sempre no topo
  const idx = gtins.findIndex(
    (g: any) => g.cod_gtin.value === cod_gtin_principal,
  );
  if (idx > 0) {
    const principalItem = gtins[idx];
    gtins.splice(idx, 1);
    gtins.unshift(principalItem);
  }

  /* ==============================
     ASSOCIADOS
  ============================== */

  const associadosRaw = apiAssociados.map((item: Associado) => ({
    cod_associado: {
      value: item.cod_produto,
      isRequired: true,
      isInvalid: false,
      invalidLabel: '',
    },
    des_produto: {
      value: item.des_produto,
      isRequired: false,
      isInvalid: false,
      invalidLabel: '',
    },
    des_departamento: {
      value: item.des_departamento,
      isRequired: false,
      isInvalid: false,
      invalidLabel: '',
    },
    flg_atual_vda_assoc: {
      value: item.flg_atual_vda_assoc,
      isRequired: false,
      isInvalid: false,
      invalidLabel: '',
    },
    per_acresc_assoc: {
      value: item.per_acresc_assoc,
      isRequired: true,
      isInvalid: false,
      invalidLabel: '',
    },
    per_desc_assoc: {
      value: item.per_desc_assoc,
      isRequired: true,
      isInvalid: false,
      invalidLabel: '',
    },
    qtd_estoque: {
      value: item.qtd_estoque,
      isInvalid: false,
      isRequired: false,
    },
  }));

  const associadosFormatted = associadosRaw.map((item: any) => ({
    id: item.cod_associado.value,
    cod_produto: item.cod_associado.value,
    des_produto: item.des_produto.value,
    des_departamento: item.des_departamento.value,
    per_desc_assoc: formatCurrencyAsText(item.per_desc_assoc.value),
    qtd_estoque: item.qtd_estoque.value,
    per_acresc_assoc: formatCurrencyAsText(item.per_acresc_assoc.value),
    flg_atual_vda_assoc: item.flg_atual_vda_assoc.value,
  }));

  /* ==============================
     EMBALAGENS
  ============================== */

  const embalagens = apiEmbalagens.map((item: Embalagem) => ({
    cod_gtin: {
      value: { label: undefined, value: item.cod_gtin },
      isRequired: true,
      isInvalid: false,
      invalidLabel: '',
    },
    des_unidade: {
      value: { label: undefined, value: item.des_unidade },
      isRequired: true,
      isInvalid: false,
      invalidLabel: '',
    },
    qtd_embalagem: {
      value: item.qtd_embalagem,
      isRequired: true,
      isInvalid: false,
      invalidLabel: '',
    },
    val_peso_emb: {
      value: numberToText(item.val_peso_emb),
      isRequired: true,
      isInvalid: false,
      invalidLabel: '',
    },
    val_peso_bruto: {
      value: numberToText(item.val_peso_bruto),
      isRequired: true,
      isInvalid: false,
      invalidLabel: '',
    },
    flg_principal: {
      value: item.flg_principal,
      isRequired: true,
      isInvalid: false,
      invalidLabel: '',
    },
    qtd_divisor_preco: {
      value: numberToText(item.qtd_divisor_preco),
      isRequired: true,
      isInvalid: false,
      invalidLabel: '',
    },
    des_unidade_medida_preco: {
      value: { label: '', value: item.des_unidade_medida_preco },
      isRequired: true,
      isInvalid: false,
      invalidLabel: '',
    },
  }));

  /* ==============================
     FORNECEDORES
  ============================== */

  const fornecedores = apiFornecedores.map((item: any) => {
    const unidades = (item.unidades || []).map((un: any) => ({
      id: { value: nanoid() },
      api_id: un.cod_item_forn_un,
      des_referencia: un.des_referencia,
      des_unidade_compra: un.des_unidade_compra,
      qtd_embalagem_compra: un.qtd_embalagem_compra,
      des_embalagem: un.des_embalagem || '',
    }));

    return {
      cod_fornecedor: { value: item.cod_fornecedor },
      consulta: { value: item.cod_fornecedor },
      des_fornecedor: { value: item.des_fornecedor },
      num_cpf_cnpj: { value: formatarCNPJ(item?.num_cpf_cnpj) },
      flg_principal: { value: item.flg_principal },
      flg_fabricante: { value: item.flg_fabricante },
      Unidades: { unidades },
    };
  });

  /* ==============================
     RETORNO FINAL PARA reset({})
  ============================== */

  return {
    /* ===== CONTROLE ===== */
    tipo_variacao:
      tipo_variacao === 1
        ? { value: 1, label: 'Possui grade' }
        : { value: 0, label: 'Não possui' },

    tipo_movimentacao: {
      label: tipo_movimentacao_view,
      value: produto.tipo_movimentacao,
    },

    tipo_associado: produtoLoja.tipo_associado,

    /* ===== IBS/CBS ===== */
    cod_ibs_cbs: {
      value: cod_ibs_cbs,
      label: produtoLoja.descricao_ibs_cbs,
    },
    per_aliq_cbs: formatCurrencyAsText(produtoLoja.per_aliq_cbs),
    per_red_cbs: formatCurrencyAsText(produtoLoja.per_red_cbs),
    val_red_social: produtoLoja.val_red_social,

    /* ===== CAMPOS SIMPLES ===== */
    cod_produto,
    des_produto: produto.des_produto,
    des_reduzida: produto.des_reduzida,
    des_sped: produto.des_tab_sped,
    cod_sped:
      produto.cod_tab_sped && produto.cod_tab_sped.toString().padStart(3, '0'),

    qtd_embalagem_compra: produto.qtd_embalagem_compra,
    qtd_embalagem_venda: produto.qtd_embalagem_venda,
    val_peso: numberToText(produto.val_peso),
    val_peso_emb: numberToText(produto.val_peso_emb),
    val_peso_bruto: numberToText(produto.val_peso_bruto),
    qtd_divisor_preco: numberToText(produto.qtd_divisor_preco),

    tecla_balanca: produtoLoja.tecla_balanca,
    qtd_etiqueta: produtoLoja.qtd_etiqueta,
    per_perda: parseFloat(produtoLoja.per_perda).toFixed(2).replace('.', ','),

    num_lote_rast: produtoLoja.num_lote_rast,
    qtd_lote_rast: produtoLoja.qtd_lote_rast,
    cod_agreg_rast: produtoLoja.cod_agreg_rast || 0,

    qtd_dias_validade: produto.qtd_dias_validade,
    qtd_est_minimo,

    dta_fabricacao_rast: produtoLoja.dta_fabricacao_rast
      ? moment(
          createDateWithoutTimezone(produtoLoja.dta_fabricacao_rast),
          'DD/MM/YYYY',
        ).format('YYYY-MM-DD')
      : null,
    des_unidade_compra_fornecedor: { label: 'UN - Unidade', value: 'UN' },
    qtd_embalagem_compra_fornecedor: 1,

    /* ===== NCM ===== */
    cod_ncm: produtoLoja.cod_ncm,

    /* ===== SIMILAR ===== */
    cod_similar:
      (!tipo_variacao && !produto.cod_similar && !des_similar) ||
      tipo_variacao === 1
        ? { label: 'Nenhum Similar Selecionado', value: undefined }
        : produto.cod_similar && des_similar
        ? { value: produto.cod_similar, label: des_similar }
        : { label: 'Nenhum Similar Selecionado', value: undefined },

    /* ===== SELECTS ===== */
    cod_departamento: lookups.departamentos.find(
      (d) => d.value === produto.cod_departamento,
    ),

    cod_tipo_item: lookups.tipoItem.find((i) => i.value === row.cod_tipo_item),

    cod_id_ctb: lookups.idContabeis.find(
      (i) => i.value === Number(row.cod_id_ctb),
    ),

    tipo_multiplicacao: lookups.tipoMultiplicacao.find(
      (i) => Number(i.value) === Number(produto.tipo_multiplicacao),
    ),

    tipo_nao_pis_cofins: lookups.tipoNaoIncidente.find(
      (i) => Number(i.value) === Number(produto.tipo_nao_pis_cofins),
    ),

    ipi_tipo: lookups.tipoIPI.find(
      (i) => Number(i.value) === Number(produto.tipo_ipi),
    ),

    ipi_val: (() => {
      const ipiVal = formatCurrencyAsText(produto.val_ipi);
      return !ipiVal ? '0,00' : ipiVal;
    })(),

    des_unidade_medida_preco: tipoDivisorPreco.find(
      (i) => String(i.value) === String(produto.des_unidade_medida_preco),
    ) ?? { label: 'UN - Unidade', value: 'UN' },

    des_unidade_compra: {
      label: `${produto.des_unidade_compra} - ${des_medida_compra}`,
      value: produto.des_unidade_compra,
    },

    des_unidade_venda: {
      label: `${produto.des_unidade_venda} - ${des_medida_venda}`,
      value: produto.des_unidade_venda,
    },

    cod_vasilhame: lookups.vasilhames?.find(
      (i) => String(i.value) === String(produtoLoja.cod_vasilhame),
    ) || { value: null, label: '' },

    cod_anp: !produto.cod_anp
      ? { label: 'Nenhum', value: null }
      : lookups.tipos_combustiveis.find((i) => i.value === produto.cod_anp),

    per_glp: produto.per_glp === 0 ? '0,00' : moneyFormat(produto.per_glp),
    per_gnn: produto.per_gnn === 0 ? '0,00' : moneyFormat(produto.per_gnn),
    per_gni: produto.per_gni === 0 ? '0,00' : moneyFormat(produto.per_gni),
    val_partida:
      produto.val_part_glp === 0 ? '0,00' : moneyFormat(produto.val_part_glp),

    des_unidade_tributavel: !produto.des_unidade_tributavel
      ? { label: 'Nenhum', value: null }
      : lookups.unCompras.find(
          (u) =>
            String(u.value).trim() ===
            String(produto.des_unidade_tributavel).trim(),
        ),

    per_veda_cred_lim:
      produtoLoja.per_veda_cred_lim === 0
        ? '0,00'
        : moneyFormat(produtoLoja.per_veda_cred_lim),

    entrada_pis_cofins: produto.cod_cred_pres
      ? lookups.pisCofins?.find(
          (option: any) => option.value === produto.cod_cred_pres,
        )
      : {
          value: null,
          label: '=Saída',
        },

    /* ===== RELAÇÕES ===== */
    cod_receita: selectedReceita,
    cod_tara: selectedTara,
    cod_info_nutricional: selectedInfoNutri,
    cod_marca: selectedMarca,

    /* ===== LISTAS ===== */
    cod_gtin_principal, // ✅ string normalizada
    gtins,
    embalagens,
    fornecedores,

    associados_pai: produtoLoja.tipo_associado === 1 ? associadosFormatted : [],

    associados_filho:
      produtoLoja.tipo_associado === 2 ? associadosFormatted : [],

    /* ===== CONTROLE FORM ===== */
    reset_pis_cofins: false,
  };
}
